import type { AiUsageSummary, AiUsageTotals } from '@/lib/api/master/ai-usage-api'
import type { ReactNode } from 'react'
import { MdWarningAmber } from 'react-icons/md'
import { useContainerWidth } from '@/lib/hooks/use-container-width'
import { Bar, BarChart, CartesianGrid, Tooltip, XAxis, YAxis } from 'recharts'
import { AI_KIND_LABELS, AI_SCOPE_LABELS, featureLabel } from '@/lib/ai/ai-usage-labels'
import {
    formatBrl,
    formatCredits,
    formatInteger,
    formatMilliCentsBrl,
    formatMinutes,
    formatPercent,
    formatTokens,
    formatUsd,
} from './ai-usage-format'

interface Props {
    summary: AiUsageSummary
}

/** Input total cobrado, cheio e em cache. */
function totalInput(row: AiUsageTotals): number {
    return row.inputTokens + row.cachedInputTokens
}

export function AiUsageOverview({ summary }: Props) {
    const { totals, usdBrlRate } = summary
    const unowned = summary.byScope.find(row => row.scope === 'UNOWNED')

    if (totals.calls === 0) {
        return (
            <div className="card bg-base-100 border border-base-300">
                <div className="card-body items-center text-center py-14">
                    <p className="font-medium">Nenhuma chamada de IA no período</p>
                    <p className="text-sm text-base-content/60 max-w-md">
                        O consumo passa a ser registrado a partir da entrega da medição. Chamadas
                        anteriores não têm histórico.
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="space-y-6">
            {summary.unpricedModels.length > 0 && (
                <div role="alert" className="alert alert-warning">
                    <MdWarningAmber className="text-xl shrink-0" />
                    <div className="text-sm">
                        <p className="font-medium">Modelos sem preço cadastrado</p>
                        <p>
                            {summary.unpricedModels
                                .map(row => `${row.model} (${pluralCalls(row.calls)})`)
                                .join(', ')}
                            . O custo dessas chamadas está fora do total: cadastre o preço na aba
                            Tabela de preços. Vale para as próximas chamadas.
                        </p>
                    </div>
                </div>
            )}

            {unowned && unowned.calls > 0 && (
                <div role="alert" className="alert alert-warning">
                    <MdWarningAmber className="text-xl shrink-0" />
                    <div className="text-sm">
                        <p className="font-medium">
                            {pluralCalls(unowned.calls)} sem dono ({formatUsd(unowned.costUsdMicros)})
                        </p>
                        <p>
                            Chegaram sem organização. É um consumidor que não passa o contexto de
                            uso; veja a lista "Sem dono" abaixo.
                        </p>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile
                    label="Custo no período"
                    value={formatUsd(totals.costUsdMicros)}
                    hint={`Cerca de ${formatBrl(totals.costUsdMicros, usdBrlRate)} (US$ 1 = R$ ${usdBrlRate.toFixed(2).replace('.', ',')})`}
                />
                <StatTile
                    label="Chamadas"
                    value={formatInteger(totals.calls)}
                    hint={`${formatTokens(totalInput(totals))} tokens de entrada, ${formatTokens(totals.outputTokens)} de saída`}
                />
                <StatTile
                    label="Entrada em cache"
                    value={formatPercent(totals.cachedInputTokens, totalInput(totals))}
                    hint="Cache é mais barato. Se cair, alguém mexeu no começo do prompt"
                />
                <StatTile
                    label="Retorno de ferramentas"
                    value={formatPercent(totals.toolResultChars, totals.inputChars)}
                    hint="Parte do texto enviado que é retorno de ferramenta, reenviado a cada turno"
                />
            </div>

            <CreditsSection summary={summary} />

            <div className="card bg-base-100 border border-base-300">
                <div className="card-body p-5 sm:p-6">
                    <h2 className="text-lg font-semibold">Custo por dia (US$)</h2>
                    <DailyCostChart summary={summary} />
                </div>
            </div>

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                <RankingTable
                    title="Por organização"
                    firstColumn="Organização"
                    rows={summary.byOrganization.map(row => ({
                        key: String(row.organizationId),
                        name: row.name ?? `Organização #${row.organizationId}`,
                        totals: row,
                    }))}
                    total={totals.costUsdMicros}
                />
                <RankingTable
                    title="Por funcionalidade"
                    firstColumn="Funcionalidade"
                    rows={summary.byFeature.map(row => ({
                        key: row.feature,
                        name: featureLabel(row.feature),
                        totals: row,
                    }))}
                    total={totals.costUsdMicros}
                />
                <RankingTable
                    title="Por modelo"
                    firstColumn="Modelo"
                    rows={summary.byModel.map(row => ({
                        key: `${row.model}:${row.kind}`,
                        name: (
                            <span>
                                <span className="font-mono text-sm">{row.model}</span>
                                <span className="text-base-content/50 text-xs ml-2">
                                    {AI_KIND_LABELS[row.kind]}
                                </span>
                            </span>
                        ),
                        totals: row,
                        extra: row.kind === 'TRANSCRIPTION' && row.audioSeconds > 0
                            ? formatMinutes(row.audioSeconds)
                            : undefined,
                    }))}
                    total={totals.costUsdMicros}
                />
                <div className="space-y-6">
                    <RankingTable
                        title="Por tipo"
                        firstColumn="Tipo"
                        rows={summary.byKind.map(row => ({
                            key: row.kind,
                            name: AI_KIND_LABELS[row.kind],
                            totals: row,
                        }))}
                        total={totals.costUsdMicros}
                    />
                    <RankingTable
                        title="Por dono do custo"
                        firstColumn="Dono"
                        rows={summary.byScope.map(row => ({
                            key: row.scope,
                            name: AI_SCOPE_LABELS[row.scope],
                            totals: row,
                        }))}
                        total={totals.costUsdMicros}
                    />
                    {summary.unownedByFeature.length > 0 && (
                        <RankingTable
                            title="Sem dono, por funcionalidade"
                            firstColumn="Funcionalidade"
                            rows={summary.unownedByFeature.map(row => ({
                                key: row.feature,
                                name: featureLabel(row.feature),
                                totals: row,
                            }))}
                            total={unowned?.costUsdMicros ?? 0}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}

/**
 * Créditos de IA: quanto foi cobrado dos tenants, quanto ficou sem cobertura e
 * o resultado sobre o custo das chamadas cobradas. Mais as organizações perto
 * do fim do saldo ou bloqueadas (estado de agora, não do período).
 */
function CreditsSection({ summary }: Props) {
    const { totals, usdBrlRate } = summary
    // `?? 0` / `?? []`: no deploy, o front novo pode falar por instantes com a API
    // de antes, que não manda estes campos.
    const lowBalance = summary.lowBalance ?? []
    const charged = totals.chargedMilliCents ?? 0
    const uncovered = totals.uncoveredMilliCents ?? 0
    if (charged === 0 && lowBalance.length === 0) return null

    const organization = summary.byScope.find(row => row.scope === 'ORGANIZATION')
    const covered = charged - uncovered
    // Custo em milésimos de centavo, só das organizações (as únicas cobradas), na
    // proporção do que foi coberto por crédito.
    const organizationCost = ((organization?.costUsdMicros ?? 0) * usdBrlRate) / 10
    const coveredCost = charged > 0 ? organizationCost * (covered / charged) : 0
    const result = covered - coveredCost

    return (
        <div className="space-y-6">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <StatTile
                    label="Cobrado em créditos"
                    value={formatMilliCentsBrl(covered)}
                    hint={`${formatCredits((totals.creditMillis ?? 0) / 1000)} créditos descontados no período`}
                />
                <StatTile
                    label="Custo do que foi cobrado"
                    value={formatMilliCentsBrl(coveredCost)}
                    hint="Custo real das chamadas cobertas por crédito, em R$"
                />
                <StatTile
                    label="Resultado"
                    value={formatMilliCentsBrl(result)}
                    hint={`Margem de ${formatPercent(result, covered)} sobre o cobrado`}
                />
                <StatTile
                    label="Sem crédito para cobrir"
                    value={formatMilliCentsBrl(uncovered)}
                    hint="Consumo de organizações fora dos créditos ou sem saldo: fica com a plataforma"
                />
            </div>

            {lowBalance.length > 0 && (
                <div className="card bg-base-100 border border-base-300">
                    <div className="card-body p-0">
                        <div className="px-5 pt-5 sm:px-6">
                            <h2 className="text-lg font-semibold">Créditos perto do fim</h2>
                            <p className="text-sm text-base-content/60">
                                Organizações com 80% ou mais do saldo gasto. Bloqueada: a IA está
                                parada até a recarga.
                            </p>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="table table-sm">
                                <thead>
                                    <tr>
                                        <th className="pl-5 sm:pl-6">Organização</th>
                                        <th className="text-right">Restante</th>
                                        <th className="text-right hidden sm:table-cell">Em vigor</th>
                                        <th className="text-right pr-5 sm:pr-6">Situação</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {lowBalance.map(row => (
                                        <tr key={row.organizationId}>
                                            <td className="pl-5 sm:pl-6">
                                                {row.name ?? `Organização #${row.organizationId}`}
                                            </td>
                                            <td className="text-right tabular-nums">
                                                {formatCredits(row.remainingCredits)}
                                            </td>
                                            <td className="text-right tabular-nums hidden sm:table-cell text-base-content/60">
                                                {formatCredits(row.grantedCredits)}
                                            </td>
                                            <td className="text-right pr-5 sm:pr-6">
                                                <span
                                                    className={`badge badge-sm ${row.blocked ? 'badge-error' : 'badge-warning'}`}
                                                >
                                                    {row.blocked ? 'Bloqueada' : 'Perto do fim'}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}

const CHART_HEIGHT = 240

/**
 * Sem `<ResponsiveContainer>`: o CSS global de `.recharts-wrapper` desfaz o
 * tamanho que ele mede e o gráfico some. Largura medida pelo `useContainerWidth`.
 */
function DailyCostChart({ summary }: Props) {
    const { ref, width } = useContainerWidth()
    const data = summary.byDay.map(row => ({
        label: `${row.day.slice(8, 10)}/${row.day.slice(5, 7)}`,
        cost: row.costUsdMicros / 1_000_000,
        calls: row.calls,
    }))

    return (
        <div ref={ref} className="w-full" style={{ height: CHART_HEIGHT, minHeight: CHART_HEIGHT }}>
            {width > 0 && (
                <BarChart
                    data={data}
                    width={width}
                    height={CHART_HEIGHT}
                    margin={{ top: 8, right: 8, left: 0, bottom: 0 }}
                >
                    <CartesianGrid vertical={false} strokeDasharray="3 3" className="opacity-30" />
                    <XAxis
                        dataKey="label"
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        minTickGap={12}
                    />
                    <YAxis
                        tick={{ fontSize: 12 }}
                        tickLine={false}
                        axisLine={false}
                        width={64}
                        tickFormatter={value => `$${Number(value).toFixed(Number(value) < 1 ? 3 : 2)}`}
                    />
                    <Tooltip
                        cursor={{ fill: 'color-mix(in oklab, var(--color-base-300) 40%, transparent)' }}
                        contentStyle={{
                            backgroundColor: 'var(--color-base-100)',
                            border: '1px solid var(--color-base-300)',
                            borderRadius: '8px',
                        }}
                        formatter={(value, _name, item) => [
                            `${formatUsd(Number(value) * 1_000_000)} em ${pluralCalls(item.payload.calls)}`,
                            'Custo',
                        ]}
                    />
                    <Bar dataKey="cost" fill="var(--color-primary)" radius={[4, 4, 0, 0]} maxBarSize={28} />
                </BarChart>
            )}
        </div>
    )
}

function pluralCalls(calls: number): string {
    return `${formatInteger(calls)} ${calls === 1 ? 'chamada' : 'chamadas'}`
}

function StatTile({ label, value, hint }: { label: string; value: string; hint: string }) {
    return (
        <div className="card bg-base-100 border border-base-300">
            <div className="card-body p-4 gap-1">
                <span className="text-sm text-base-content/60">{label}</span>
                <span className="text-2xl font-semibold tabular-nums">{value}</span>
                <span className="text-xs text-base-content/50">{hint}</span>
            </div>
        </div>
    )
}

interface RankingRow {
    key: string
    name: ReactNode
    totals: AiUsageTotals
    extra?: string
}

function RankingTable({
    title,
    firstColumn,
    rows,
    total,
}: {
    title: string
    firstColumn: string
    rows: RankingRow[]
    total: number
}) {
    return (
        <div className="card bg-base-100 border border-base-300">
            <div className="card-body p-0">
                <h2 className="text-lg font-semibold px-5 pt-5 sm:px-6">{title}</h2>
                <div className="overflow-x-auto">
                    <table className="table table-sm">
                        <thead>
                            <tr>
                                <th className="pl-5 sm:pl-6">{firstColumn}</th>
                                <th className="text-right">Chamadas</th>
                                <th className="text-right hidden sm:table-cell">Tokens</th>
                                <th className="text-right">Custo</th>
                                <th className="text-right pr-5 sm:pr-6">Parcela</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(row => (
                                <tr key={row.key}>
                                    <td className="pl-5 sm:pl-6">{row.name}</td>
                                    <td className="text-right tabular-nums">
                                        {formatInteger(row.totals.calls)}
                                    </td>
                                    <td className="text-right tabular-nums hidden sm:table-cell">
                                        {row.extra ??
                                            formatTokens(
                                                totalInput(row.totals) + row.totals.outputTokens
                                            )}
                                    </td>
                                    <td className="text-right tabular-nums">
                                        {formatUsd(row.totals.costUsdMicros)}
                                        {row.totals.unpricedCalls > 0 && (
                                            <span
                                                className="text-warning ml-1"
                                                title={`${pluralCalls(row.totals.unpricedCalls)} sem preço cadastrado`}
                                            >
                                                *
                                            </span>
                                        )}
                                    </td>
                                    <td className="text-right tabular-nums pr-5 sm:pr-6 text-base-content/60">
                                        {formatPercent(row.totals.costUsdMicros, total)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}
